<template>
  <div class="app-shell home-page tool-page">
    <div class="app-shell__main">
      <AppHeader />

      <main class="home-main tool-main">
        <section class="hero-section tool-hero-section tool-page-hero">
          <div class="tool-hero-grid">
            <div class="tool-hero-copy">
              <span class="tool-eyebrow">Cutout Pro</span>
              <h1 class="hero-title">人像抠图</h1>
              <p class="hero-description">智能识别主体并移除背景，适合商品图、人像图和海报素材。</p>
              <div class="tool-copy-pills">
                <span>自动识别主体与背景</span>
                <span>支持通用与动漫两种模式</span>
                <span>透明底图可直接保存</span>
              </div>
              <div class="tool-hero-summary">
                <article class="tool-hero-stat">
                  <strong>上传后自动识别</strong>
                  <span>自动区分主体和背景，减少手动抠图步骤。</span>
                </article>
                <article class="tool-hero-stat">
                  <strong>支持两种识别模式</strong>
                  <span>可按普通图片或动漫图片选择更合适的处理方式。</span>
                </article>
                <article class="tool-hero-stat">
                  <strong>结果更适合复用</strong>
                  <span>透明底图适合电商主图、海报设计和社媒内容整理。</span>
                </article>
              </div>
            </div>

            <div class="tool-workbench tool-workbench--link">
              <!-- <div class="tool-stage-note">
                <div class="tool-stage-note__title">抠图工作台</div>
                <p>上传图片后一键开始，处理完成后可直接查看和下载透明背景结果。</p>
              </div> -->
              <div class="image-remove-bg-container">
                <div
                  v-if="!imageUrl"
                  class="upload-area"
                  :class="{ 'is-drag-active': isDragActive }"
                  @click="triggerUpload"
                  @dragenter.prevent="handleDragEnter"
                  @dragover.prevent="handleDragOver"
                  @dragleave.prevent="handleDragLeave"
                  @drop.prevent="handleDrop"
                >
                  <input type="file" ref="fileInput" accept="image/*" class="hidden-input" @change="handleFileChange" />
                  <div class="upload-placeholder">
                    <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <span class="upload-text">点击上传图片</span>
                    <span class="upload-hint">支持 JPG / PNG 格式，也可直接拖入图片</span>
                  </div>
                </div>

                <div v-else class="editor-area">
                  <div class="canvas-wrapper" ref="canvasWrapper" @contextmenu.prevent>
                    <img ref="imageRef" :src="imageUrl" class="editor-image" crossorigin="anonymous" />
                  </div>

                  <div class="toolbar">
                    <div class="tool-group">
                      <button class="tool-btn" :class="{ active: !isAnimType }" @click="isAnimType = false">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                        <span>通用</span>
                      </button>
                      <button class="tool-btn" :class="{ active: isAnimType }" @click="isAnimType = true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                          <line x1="9" y1="9" x2="9.01" y2="9"></line>
                          <line x1="15" y1="9" x2="15.01" y2="9"></line>
                        </svg>
                        <span>动漫</span>
                      </button>
                    </div>

                    <div class="divider"></div>

                    <div class="tool-group">
                      <button class="tool-btn" @click="undo" :disabled="historyStep <= 0">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M3 7v6h6"></path>
                          <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
                        </svg>
                        <span>撤销</span>
                      </button>
                      <button class="tool-btn" @click="redo" :disabled="historyStep >= history.length - 1">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M21 7v6h-6"></path>
                          <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"></path>
                        </svg>
                        <span>恢复</span>
                      </button>
                    </div>

                    <div class="divider"></div>

                    <div class="tool-group action-group">
                      <button class="tool-btn secondary" @click="resetImage">重选图片</button>
                      <button class="tool-btn primary" @click="removeBackground" :disabled="isProcessing">
                        <span v-if="isProcessing">处理中...</span>
                        <span v-else>人像抠图</span>
                      </button>
                      <button class="tool-btn success" @click="saveImageResult" :disabled="!processedImage">保存结果</button>
                    </div>
                  </div>
                </div>

                <p class="hint-text">点击“人像抠图”即可自动抠图。</p>
              </div>
            </div>

            <div class="learn-more">
              <a href="#how-it-works" class="learn-more-link">
                <div class="learn-more-text">了解更多</div>
                <svg class="arrow-down" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </a>
            </div>
          </div>
        </section>

        <section id="how-it-works" class="how-it-works-section tool-info-section">
          <div class="container">
            <div class="tool-section-head">
              <h2 class="section-title">如何使用</h2>
              <p>上传图片后一键开始，快速拿到更干净的透明底图。</p>
            </div>
            <div class="steps-grid">
              <div class="step-card">
                <div class="step-number">1</div>
                <h3 class="step-title">上传图片</h3>
                <p class="step-description">选择需要抠图的图片。</p>
              </div>
              <div class="step-card">
                <div class="step-number">2</div>
                <h3 class="step-title">自动识别</h3>
                <p class="step-description">AI 自动识别主体与背景。</p>
              </div>
              <div class="step-card">
                <div class="step-number">3</div>
                <h3 class="step-title">下载保存</h3>
                <p class="step-description">保存透明底图或处理结果。</p>
              </div>
            </div>
          </div>
        </section>

        <section id="features" class="features-section tool-info-section">
          <div class="container">
            <div class="tool-section-head">
              <h2 class="section-title">功能特点</h2>
              <p>围绕抠图和导出场景，把说明压缩到更好懂的表达里。</p>
            </div>
            <div class="features-grid">
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"></path>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                </div>
                <h3 class="feature-title">自动处理</h3>
                <p class="feature-description">上传后即可自动完成抠图。</p>
              </div>
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <h3 class="feature-title">多场景适用</h3>
                <p class="feature-description">适合人像、商品和社媒配图。</p>
              </div>
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                  </svg>
                </div>
                <h3 class="feature-title">发丝级抠图</h3>
                <p class="feature-description">边缘更细致，主体更完整。</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import request from '@/utils/request'
import { getRemoveKey } from '@/utils/crypto'
import OSS from '@/utils/lib/aliyun-oss-sdk-6.17.1.min.js'
window.OSS = OSS;
import '@/utils/aliyun-upload-sdk-1.5.7.min.js'
import { showDialog, showConfirmDialog, showToast, showLoadingToast, closeToast } from 'vant'

export default {
  name: 'RemoveBg',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      imageUrl: null, // Display URL (local blob or remote)
      remoteImageUrl: null, // URL for backend (from upload response)
      processedImage: null, // Result image URL (Base64 or remote)
      
      // Loading
      isUploading: false,
      isProcessing: false,
      isDragActive: false,
      dragDepth: 0,

      imageType: 'jpg', // Default image type

      // VOD Upload
      uploader: null,
      vodUrl: "https://vod-sz.adaiyun.net/",
      
      isAnimType: false, // Placeholder for future feature
      
      // History
      history: [],
      historyStep: -1,

      // Download State
      downloadingItems: new Set(),
      downloadProgress: {},
    }
  },
  mounted() {
    this.initVod()
  },
  methods: {
    initVod() {
      const that = this
      // Ensure globals are available
      if (!window.AliyunUpload || !window.AliyunUpload.Vod) {
        console.error('AliyunUpload SDK not loaded')
        return
      }
      this.uploader = new window.AliyunUpload.Vod({
        userId: "1048463214720895",
        region: "cn-shenzhen",
        partSize: 1048576,
        parallel: 5,
        retryCount: 3,
        retryDuration: 2,
        onUploadstarted: async function (uploadInfo) {
          console.log('onUploadstarted', uploadInfo)
          let url = "/aliyun/vod/create/upload-image-auth"
          let data = {
            title: uploadInfo.file.name,
            imageType: 'default'
          }

          try {
            const res = await request(url, {
              method: 'POST',
              body: data
            })

            if (res) {
              const { uploadAuth, uploadAddress, videoId } = res
              that.uploader.setUploadAuthAndAddress(uploadInfo, uploadAuth, uploadAddress, videoId)
            } else {
              console.error('Failed to get upload auth', res)
              that.isUploading = false
            }
          } catch (err) {
            console.error('Error getting upload auth', err)
            that.isUploading = false
          }
        },
        onUploadSucceed: function (uploadInfo) {
          console.log('onUploadSucceed', uploadInfo)
          const imageUrl = that.vodUrl + uploadInfo.object
          that.remoteImageUrl = imageUrl
          
          // Update current history state with remote URL if it matches the current image
          if (that.historyStep >= 0 && that.history[that.historyStep].imageUrl === that.imageUrl) {
             that.history[that.historyStep].remoteImageUrl = imageUrl
          }
          
          that.isUploading = false
          console.log('Upload success, remote URL:', imageUrl)
        },
        onUploadFailed: function (uploadInfo, code, message) {
          console.error('onUploadFailed', uploadInfo, code, message)
          that.isUploading = false
          alert('图片上传失败: ' + message)
        },
        onUploadProgress: function (uploadInfo, totalSize, loadedPercent) {
          console.log('onUploadProgress', uploadInfo, totalSize, loadedPercent)
        },
        onUploadTokenExpired: function (uploadInfo) {
          console.log('onUploadTokenExpired', uploadInfo)
        }
      })
    },

    triggerUpload() {
      const token = localStorage.getItem('token')
      if (!token) {
        console.log('token失效或不可用')
        window.dispatchEvent(new CustomEvent('auth-login-required', { detail: { msg: '登录失效或不可用，请重新登录' } }))
        return
      }
      this.$refs.fileInput.click()
    },

    async handleFileChange(e) {
      const file = e.target.files[0]
      e.target.value = ''
      if (!file) return
      await this.processSelectedFile(file)
    },

    handleDragEnter() {
      this.dragDepth += 1
      this.isDragActive = true
    },

    handleDragOver(e) {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy'
      }
      this.isDragActive = true
    },

    handleDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) {
        this.isDragActive = false
      }
    },

    async handleDrop(e) {
      this.dragDepth = 0
      this.isDragActive = false
      const file = e.dataTransfer?.files?.[0]
      if (!file) return
      await this.processSelectedFile(file)
    },

    async processSelectedFile(file) {
      if (!file.type.startsWith('image/')) {
        showToast('请拖入图片文件')
        return
      }

      // Local preview
      this.imageUrl = URL.createObjectURL(file)

      // Get image type from filename
      const extension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase()
      this.imageType = extension || 'jpg'

      this.processedImage = null
      this.remoteImageUrl = null

      // Upload
      this.isUploading = true
      
      // Initialize history with original image
      this.history = []
      this.historyStep = -1
      this.pushHistory()

      if (this.uploader) {
        this.uploader.addFile(file, null, null, null, '{"Vod":{}}')
        this.uploader.startUpload()
      } else {
        console.error('Uploader not initialized')
        this.isUploading = false
        alert('上传组件初始化失败，请刷新重试')
      }
    },

    resetImage() {
      this.imageUrl = null
      this.remoteImageUrl = null
      this.processedImage = null
      this.isDragActive = false
      this.dragDepth = 0
      this.history = []
      this.historyStep = -1
    },

    pushHistory() {
      const state = {
        imageUrl: this.imageUrl,
        remoteImageUrl: this.remoteImageUrl,
        processedImage: this.processedImage
      }
      
      // Remove any future history if we are in the middle
      if (this.historyStep < this.history.length - 1) {
        this.history = this.history.slice(0, this.historyStep + 1)
      }
      
      this.history.push(state)
      this.historyStep++
    },

    undo() {
      if (this.historyStep > 0) {
        this.historyStep--
        this.restoreState(this.history[this.historyStep])
      }
    },

    redo() {
      if (this.historyStep < this.history.length - 1) {
        this.historyStep++
        this.restoreState(this.history[this.historyStep])
      }
    },

    restoreState(state) {
      this.imageUrl = state.imageUrl
      this.remoteImageUrl = state.remoteImageUrl
      this.processedImage = state.processedImage
    },

    async removeBackground() {
      if (!this.imageUrl || this.isProcessing) return
      
      if (!this.remoteImageUrl) {
        showToast('图片正在上传中，请稍候...')
        return
      }

      this.isProcessing = true
      showLoadingToast({
        message: '智能抠图中...',
        forbidClick: true,
        duration: 0
      })

      try {
        // 1. Get Key
        const keyRes = await request('/remove-watermark/create-key', { 
            method: 'POST',
            body: { type: 'SEGMENT_HUMAN' }
        })

        if (!keyRes || !keyRes.data) throw new Error('Failed to get key')

        const key = getRemoveKey(keyRes.data, keyRes.timestamp)

        // 2. Call Removal API
        const url = this.isAnimType ? '/painting/rembg-anim' : '/painting/rembg'
        
        const removeRes = await request(url, {
          method: 'POST',
          // contentType: 'application/json',
          body: {
            imageUrl: this.remoteImageUrl,
            imageType: this.imageType,
            key: key
          }
        })

        // Success
        let resultBase64 = removeRes
        if (!resultBase64) {
          throw new Error('Result image is empty')
        }
        
        // Handle Base64 prefix
        if (!resultBase64.startsWith('data:image')) {
            resultBase64 = 'data:image/png;base64,' + resultBase64
        }

        this.processedImage = resultBase64
        this.imageUrl = resultBase64 // Display result
        this.pushHistory()

        showToast('抠图成功')

      } catch (error) {
        console.error('Remove background failed', error)
        showDialog({ message: error.message || '人像抠图失败' })
      } finally {
        this.isProcessing = false
        closeToast()
      }
    },

    async saveImageResult(forceUseStatus = 0) {
      if (!this.processedImage) return
      
      const status = Number(forceUseStatus) || 0
      
      showLoadingToast({
        message: '保存中...',
        forbidClick: true,
        duration: 0
      })

      try {
        const keyRes = await request('/remove-watermark/create-key', { 
            method: 'POST',
            body: { type: 'SEGMENT_HUMAN' }
        })

        if (!keyRes || !keyRes.data) throw new Error('Failed to get key')

        const key = getRemoveKey(keyRes.data, keyRes.timestamp)

        // 4. Call Save API
        const createOneRes = await request('/painting/rembg/create-one', {
            method: 'POST',
            // contentType: 'application/json',
            body: {
            key: key,
            forceUseStatus: status
            }
        })

        closeToast()
        console.log(createOneRes)
        
        showConfirmDialog({
          title: '保存图片',
          message: '是否直接下载？或拷贝链接到浏览器打开',
          confirmButtonText: '直接下载',
          cancelButtonText: '拷贝链接',
        }).then(() => {
          this.downloadImage(this.processedImage)
        })
          .catch((error) => {
            if (error === 'cancel') {
              this.copyToClipboard(createOneRes)
            }
          })
          
      } catch (error) {
        closeToast()
        console.error('Save image failed', error)
        if (error.code == 4010) {
            showConfirmDialog({ message: '积分不足，成为会员后再尝试' })
            .then(() => { this.$router.push({ name: 'WebPay'}) })
            .catch(() => {})
        } else if (error.code == 405) {
            showConfirmDialog({ message: '已达会员每日次数使用上限，是否继续使用积分？' })
            .then(() => { this.saveImageResult(1) })
            .catch(() => {})
        } else if (error.code == 406) {
            showConfirmDialog({ message: '已达会员每日100次使用上限且无可用积分，是否需要获取更多积分？' })
            .then(() => { this.$router.push({ name: 'WebPay', query: { payStatus: 1 } }) })
            .catch(() => {})
        } else {
            showDialog({ message: error.message || '保存失败' })
        }
      }
    },

    async downloadImage(url, type = 'image', httpHeaders = null) {
      const downloadKey = `${type}_${url}`
      if (this.downloadingItems.has(downloadKey)) {
        return
      }

      this.downloadingItems.add(downloadKey)
      this.updateDownloadProgress(downloadKey, 0)

      try {
        // Check Range support
        const supportsRange = await this.checkRangeSupport(url, httpHeaders)

        if (supportsRange) {
          console.log('Server supports Range, using multi-thread download')
          await this.downloadImageWithMultiThread(url, type, 1024 * 1024, httpHeaders)
        } else {
          console.log('Server does not support Range, using single-thread download')
          await this.downloadImageSingleThread(url, type, httpHeaders)
        }
      } catch (error) {
        console.error('Download failed:', error)
        this.downloadProgress[downloadKey] = { progress: 0, status: 'error' }
        showToast('下载失败，请尝试直接打开链接')
        
        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 3000)
      }
    },

    // Get Referer
    getRefererFromUrl(url) {
      try {
        const urlObj = new URL(url)
        return `${urlObj.protocol}//${urlObj.hostname}/`
      } catch (error) {
        console.warn('Cannot parse URL:', error)
        return ''
      }
    },

    // Check Range Support
    async checkRangeSupport(url, httpHeaders = null) {
      try {
        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const fetchOptions = {
          method: 'HEAD',
          headers: headers,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const response = await fetch(url, fetchOptions)
        return response.headers.get('accept-ranges') === 'bytes'
      } catch {
        return false
      }
    },

    // Update Progress
    updateDownloadProgress(downloadKey, progress, speed = null, status = 'downloading') {
      this.downloadProgress[downloadKey] = {
        progress,
        status,
        speed: speed ? `${(speed / 1024 / 1024).toFixed(1)} MB/s` : null
      }
      
      // Show global toast
      if (status === 'downloading') {
        const speedText = speed ? `${(speed / 1024 / 1024).toFixed(1)} MB/s` : ''
        showLoadingToast({
          message: `下载中 ${progress}% ${speedText}`,
          forbidClick: true,
          duration: 0
        })
      } else if (status === 'completed') {
        closeToast()
        showToast('下载完成')
      } else if (status === 'error') {
        closeToast()
        showToast('下载失败')
      }
    },

    // Trigger Download
    triggerDownload(blob, filename) {
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()

      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      }, 1000)
    },

    // Multi-thread Download
    async downloadImageWithMultiThread(url, type, chunkSize = 1024 * 1024, httpHeaders = null) {
      const downloadKey = `${type}_${url}`

      try {
        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const headFetchOptions = {
          method: 'HEAD',
          headers: headers,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const headResponse = await fetch(url, headFetchOptions)
        const fileSize = parseInt(headResponse.headers.get('content-length'))

        if (!fileSize || fileSize < chunkSize * 2) {
          return this.downloadImageSingleThread(url, type, httpHeaders)
        }

        const chunkCount = Math.ceil(fileSize / chunkSize)
        const maxConcurrent = Math.min(chunkCount, 6)

        this.updateDownloadProgress(downloadKey, 0, null, 'downloading')

        const chunks = new Array(chunkCount)
        const downloadedChunks = new Set()
        const startTime = Date.now()
        let downloadedBytes = 0

        for (let batchStart = 0; batchStart < chunkCount; batchStart += maxConcurrent) {
          const batchEnd = Math.min(batchStart + maxConcurrent, chunkCount)
          const batchPromises = []

          for (let i = batchStart; i < batchEnd; i++) {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize - 1, fileSize - 1)

            const downloadChunk = async () => {
              const chunkHeaders = { ...headers, 'Range': `bytes=${start}-${end}` }
              const chunkFetchOptions = {
                headers: chunkHeaders,
                referrer: referrer,
                referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
              }
              const response = await fetch(url, chunkFetchOptions)

              if (!response.ok) {
                throw new Error(`Chunk download failed: ${response.status}`)
              }

              const arrayBuffer = await response.arrayBuffer()
              chunks[i] = new Uint8Array(arrayBuffer)
              downloadedChunks.add(i)
              downloadedBytes += arrayBuffer.byteLength

              const elapsed = (Date.now() - startTime) / 1000
              const speed = downloadedBytes / elapsed
              const progress = Math.round((downloadedChunks.size / chunkCount) * 100)

              this.updateDownloadProgress(downloadKey, progress, speed)
            }

            batchPromises.push(downloadChunk())
          }

          await Promise.all(batchPromises)
        }

        this.updateDownloadProgress(downloadKey, 95, null, 'finalizing')

        const mergedArray = new Uint8Array(fileSize)
        let offset = 0
        for (const chunk of chunks) {
          if (chunk) {
            mergedArray.set(chunk, offset)
            offset += chunk.length
          }
        }

        const blob = new Blob([mergedArray])
        const filename = `rembg_${new Date().getTime()}.png`

        this.triggerDownload(blob, filename)
        this.updateDownloadProgress(downloadKey, 100, null, 'completed')

        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 2000)

      } catch (error) {
        console.error('Multi-thread download failed, fallback to single-thread:', error)
        return this.downloadImageSingleThread(url, type, httpHeaders)
      }
    },

    // Single-thread Download
    async downloadImageSingleThread(url, type, httpHeaders = null) {
      const downloadKey = `${type}_${url}`

      try {
        const filename = `rembg_${new Date().getTime()}.png`

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 300000)

        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const fetchOptions = {
          method: 'GET',
          headers: headers,
          signal: controller.signal,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const response = await fetch(url, fetchOptions)

        clearTimeout(timeoutId)

        if (!response.ok) {
          throw new Error(`Download failed: HTTP ${response.status}`)
        }

        const contentLength = response.headers.get('content-length')
        if (contentLength) {
          const fileSize = parseInt(contentLength, 10)
          const fileSizeMB = fileSize / (1024 * 1024)
          if (fileSizeMB > 100) {
            console.warn(`Large file: ${fileSizeMB.toFixed(1)}MB`)
          }
        }

        this.updateDownloadProgress(downloadKey, 50, null, 'processing')
        const blob = await response.blob()
        this.updateDownloadProgress(downloadKey, 80, null, 'finalizing')
        this.triggerDownload(blob, filename)
        this.updateDownloadProgress(downloadKey, 100, null, 'completed')

        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 2000)

      } catch (error) {
        console.error('Single-thread download failed:', error)
        this.downloadProgress[downloadKey] = { progress: 0, status: 'error' }
        
        if (error.name === 'AbortError') {
          showToast('下载超时')
        } else {
          showToast('下载失败')
        }

        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 3000)
      }
    },

    copyToClipboard(text) {
        navigator.clipboard.writeText(text).then(() => {
            showToast('链接已复制')
        }).catch(() => {
            showToast('复制失败，请手动复制')
        })
    }
  }
}
</script>

<style scoped>
.tool-page .home-main {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
}

.tool-page .hero-section.tool-page-hero {
  padding: 28px;
}

.tool-page .tool-workbench--link {
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.tool-page .container,
.image-remove-bg-container {
  width: 100%;
  max-width: none;
  margin: 0;
}

.image-remove-bg-container {
  padding: 28px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 24px;
  box-shadow: 0 18px 45px rgba(50, 71, 117, 0.08);
}

.hidden-input {
  display: none;
}

.upload-area {
  width: 100%;
  min-height: 320px;
  border: 2px dashed rgba(79, 94, 122, 0.18);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: linear-gradient(180deg, rgba(247, 249, 252, 0.88), rgba(255, 255, 255, 0.92));
  transition: 0.2s ease;
}

.upload-area:hover {
  border-color: rgba(79, 70, 229, 0.32);
  background: rgba(79, 70, 229, 0.04);
}

.upload-area.is-drag-active {
  border-color: rgba(79, 70, 229, 0.48);
  background: rgba(79, 70, 229, 0.08);
  box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.12);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-icon {
  width: 52px;
  height: 52px;
  color: var(--brand);
}

.upload-text {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}

.upload-hint {
  font-size: 14px;
  color: var(--muted);
}

.editor-area {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.canvas-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #0f172a;
  border-radius: 24px;
  overflow: hidden;
}

.editor-image {
  display: block;
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
}

.toolbar {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px;
  background: rgba(247, 249, 252, 0.9);
  border: 1px solid rgba(223, 230, 242, 0.92);
  border-radius: 20px;
}

.tool-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.action-group {
  margin-left: auto;
}

.tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid rgba(223, 230, 242, 0.96);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 14px;
  cursor: pointer;
  font-weight: 600;
  color: var(--ink);
  transition: 0.2s ease;
}

.tool-btn svg {
  width: 18px;
  height: 18px;
}

.tool-btn:hover:not(:disabled),
.tool-btn.active {
  border-color: rgba(79, 70, 229, 0.28);
  color: var(--brand-deep);
  background: rgba(79, 70, 229, 0.06);
}

.tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tool-btn.primary {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, #5b6bff 0%, #2f80ed 100%);
}

.tool-btn.primary:hover:not(:disabled) {
  color: #fff;
}

.tool-btn.success {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, #16a34a 0%, #10b981 100%);
}

.tool-btn.secondary {
  background: rgba(248, 250, 252, 0.95);
}

.divider {
  width: 1px;
  height: 28px;
  background: rgba(223, 230, 242, 0.96);
}

.hint-text {
  margin: 16px 0 0;
  color: var(--muted);
  line-height: 1.7;
}

.tool-page .tool-info-section .container {
  width: 100%;
}

.tool-page .steps-grid,
.tool-page .features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  padding: 0;
}

.tool-page .step-card,
.tool-page .feature-card {
  padding: 24px;
  text-align: left;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(223, 230, 242, 0.92);
  border-radius: 24px;
  box-shadow: 0 14px 28px rgba(50, 71, 117, 0.06);
  transition: 0.2s ease;
}

.tool-page .step-card:hover,
.tool-page .feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 34px rgba(50, 71, 117, 0.1);
}

.step-number {
  width: 48px;
  height: 48px;
  margin: 0 0 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #5b6bff 0%, #2f80ed 100%);
}

.feature-icon {
  width: 52px;
  height: 52px;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  color: var(--brand);
  background: rgba(79, 70, 229, 0.1);
}

.feature-icon svg {
  width: 24px;
  height: 24px;
}

.step-title,
.feature-title {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  color: var(--ink);
}

.step-description,
.feature-description {
  margin: 0;
  color: var(--muted);
  line-height: 1.8;
}

.tool-page .learn-more {
  display: flex;
  justify-content: center;
}

.tool-page .section-title {
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .tool-page .hero-section.tool-page-hero {
    padding: 20px;
  }

  .image-remove-bg-container {
    padding: 20px;
  }

  .toolbar {
    padding: 14px;
  }

  .action-group {
    margin-left: 0;
  }

  .divider {
    display: none;
  }
}
</style>
